import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  BalanceQuery,
  config,
  MemberDetails,
  MemberQuery,
  RewardsWalletQuery,
  RewardsWalletService,
  ScreenType,
  WalletGroupItem,
} from '@fgb/core';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { firstValueFrom, Observable } from 'rxjs';
import { Populations } from 'src/app/shared/utilities/populations';

gsap.registerPlugin(Draggable);

/** Mobile-only breakpoint the draggable sheet is enabled below (matches $breakpoint-lg). */
const MOBILE_MAX_WIDTH = 991;
/** Height of the fixed WALLET header the sheet docks just beneath (matches .page-header). */
const HEADER_HEIGHT = 60;
/** Body class used to hide the global master footer while this sheet is active. */
const SHEET_ACTIVE_CLASS = 'wallet-sheet-active';

@Component({
  selector: 'fgb-wallet-page',
  templateUrl: './wallet-page.component.html',
  styleUrls: ['./wallet-page.component.scss'],
})
export class WalletPageComponent implements OnInit, AfterViewInit, OnDestroy {
  walletItems$: Observable<WalletGroupItem[]>;
  populations = Populations;
  screenSize = ScreenType;
  member$: Observable<MemberDetails | undefined>;
  ecash$: Observable<number | undefined>;
  memberDetails$: Observable<MemberDetails | undefined>;
  pointsBalance$: Observable<number | undefined>;

  @ViewChild('sheet') sheet?: ElementRef<HTMLDivElement>;
  @ViewChild('sheetHandle') sheetHandle?: ElementRef<HTMLDivElement>;
  @ViewChild('sheetContent') sheetContent?: ElementRef<HTMLDivElement>;

  private draggable?: Draggable;
  private mm = gsap.matchMedia();
  /** Distance (px) the sheet sits below its docked top when collapsed. */
  private collapsedOffset = 0;

  constructor(
    private rewardsWalletQuery: RewardsWalletQuery,
    private rewardsWalletService: RewardsWalletService,
    private memberDetailsQuery: MemberQuery,
    private balanceQuery: BalanceQuery
  ) {}

  async ngOnInit() {
    firstValueFrom(this.rewardsWalletService.fetchWalletItems());
    this.walletItems$ = this.rewardsWalletQuery.selectGroupedUnredeemedWalletItems();
    this.member$ = this.memberDetailsQuery.selectMemberDetails();

    this.pointsBalance$ = this.balanceQuery.selectPurse(config.purseConfig.virtualLoyalty);
    this.ecash$ = this.balanceQuery.selectPurse(config.purseConfig.combined);
  }

  ngAfterViewInit(): void {
    // Only enable the draggable bottom-sheet on mobile, and tear it down on resize back to desktop.
    this.mm.add(`(max-width: ${MOBILE_MAX_WIDTH}px)`, () => {
      this.initDraggableSheet();
      return () => this.destroyDraggableSheet();
    });
  }

  private initDraggableSheet(): void {
    const sheetEl = this.sheet?.nativeElement;
    const handleEl = this.sheetHandle?.nativeElement;
    const contentEl = this.sheetContent?.nativeElement;
    if (!sheetEl || !handleEl || !contentEl) {
      return;
    }

    // Hide the global master footer on mobile so only the sheet's own footer shows.
    document.body.classList.add(SHEET_ACTIVE_CLASS);

    // Start from a known scroll position — navigating here from a scrolled page would
    // otherwise leave the fixed overlay measured against a stale scroll offset.
    window.scrollTo(0, 0);

    // Resting = the sheet's natural spot, just below the blue wallet panel above it.
    // Dragging the handle UP lifts it by that panel's height so its top reaches the header
    // (just below the navbar). Panel height is a stable, scroll-independent measurement.
    const wrapperEl = sheetEl.previousElementSibling as HTMLElement | null;
    const restingTop = HEADER_HEIGHT + (wrapperEl?.offsetHeight ?? 0);
    this.collapsedOffset = Math.max(restingTop - HEADER_HEIGHT, 0);

    gsap.set(sheetEl, {
      position: 'fixed',
      left: 0,
      right: 0,
      top: restingTop,
      bottom: 0,
      zIndex: 3,
      y: 0, // start at rest; drag up (toward -collapsedOffset) to dock under the navbar
    });

    // Content only scrolls once docked at the top; locked while collapsed.
    this.setContentScrollable(false);

    this.draggable = Draggable.create(sheetEl, {
      type: 'y',
      trigger: handleEl,
      bounds: { minY: -this.collapsedOffset, maxY: 0 },
      inertia: false,
      cursor: 'grab',
      activeCursor: 'grabbing',
      onDragEnd: () => this.snap(),
    })[0];
  }

  /** Snap to docked (up, just below the navbar, scrollable) or resting — whichever is nearer. */
  private snap(): void {
    const sheetEl = this.sheet?.nativeElement;
    if (!sheetEl || !this.draggable) {
      return;
    }

    // y ranges from -collapsedOffset (docked) to 0 (resting). Snap to the nearer end.
    const shouldDock = this.draggable.y < -this.collapsedOffset / 2;
    const target = shouldDock ? -this.collapsedOffset : 0;

    gsap.to(sheetEl, {
      y: target,
      duration: 0.3,
      ease: 'power2.out',
      onUpdate: () => {
        this.draggable?.update();
      },
      onComplete: () => this.setContentScrollable(shouldDock),
    });
  }

  /** Enable internal scroll only when docked, so the content scrolls down to the footer. */
  private setContentScrollable(scrollable: boolean): void {
    const contentEl = this.sheetContent?.nativeElement;
    if (!contentEl) {
      return;
    }
    contentEl.style.overflowY = scrollable ? 'auto' : 'hidden';
    contentEl.style.touchAction = scrollable ? 'pan-y' : 'none';
    if (!scrollable) {
      contentEl.scrollTop = 0;
    }
  }

  private destroyDraggableSheet(): void {
    document.body.classList.remove(SHEET_ACTIVE_CLASS);
    this.draggable?.kill();
    this.draggable = undefined;

    const sheetEl = this.sheet?.nativeElement;
    if (sheetEl) {
      gsap.set(sheetEl, { clearProps: 'transform,position,left,right,top,bottom,zIndex' });
    }
    const contentEl = this.sheetContent?.nativeElement;
    if (contentEl) {
      contentEl.style.removeProperty('overflow-y');
      contentEl.style.removeProperty('touch-action');
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove(SHEET_ACTIVE_CLASS);
    this.mm.kill();
  }
}
